<template>
  <div class="dropdown-textfield__wrapper">
    <div class="dropdown-textfield" :class="{ reverse }">
      <ComboboxRoot
        :modelValue="option"
        @update:modelValue="(v: any) => $emit('update:option', v)"
        :display-value="displayValue"
        :disabled="disableOption"
      >
        <ComboboxAnchor class="ComboboxAnchor" :class="{ optionDisabled: disableOption }">
          <ComboboxTrigger class="ComboboxTrigger">
            <slot name="dropdown-start" v-if="$slots['dropdown-start']" />
            <ComboboxInput class="ComboboxInput" :placeholder="optionPlaceholder" />
            <iconify-icon icon="fa6-solid:caret-down" class="ComboboxIcon" />
          </ComboboxTrigger>
        </ComboboxAnchor>

        <ComboboxContent class="WDropdownTextfieldContent ComboboxContent" position="popper">
          <ComboboxViewport class="ComboboxViewport">
            <ComboboxItem class="ComboboxItem" v-for="(_option, index) in options" :key="index" :value="_option?.value">
              <slot name="option" :option="_option">
                <span>{{ _option?.label }}</span>
              </slot>
            </ComboboxItem>
          </ComboboxViewport>
        </ComboboxContent>
      </ComboboxRoot>

      <div class="vl"></div>
      <div class="input-wrapper" :class="{ focused: active || !['', null].includes(modelValue) }">
        <label class="flex smaller description">
          <span>
            {{ placeholder }}
          </span>
        </label>
        <input
          :type="'text'"
          name=""
          id=""
          @keyup.enter="verification()"
          :value="formattedValue"
          @focusin="active = true"
          @focusout="active = false"
          @blur="$emit('blur', $event)"
          @input="handleInput"
          :maxlength="max"
          :max="max"
        />
      </div>
    </div>

    <!-- Error message -->
    <div v-if="localError" class="smaller communication-danger dropdown-textfield__error">
      <span>
        {{ localError }}
      </span>
    </div>
  </div>
</template>

<script lang="ts">
  import * as R from 'radix-vue';

  export default {
    name: 'DropdownTextfield',
    emits: ['update:modelValue', 'update:option', 'enter', 'blur', 'change'],
    components: {
      ComboboxLabel: R.ComboboxLabel,
      ComboboxAnchor: R.ComboboxAnchor,
      ComboboxItem: R.ComboboxItem,
      ComboboxInput: R.ComboboxInput,
      ComboboxTrigger: R.ComboboxTrigger,
      ComboboxContent: R.ComboboxContent,
      ComboboxViewport: R.ComboboxViewport,
      ComboboxEmpty: R.ComboboxEmpty,
      ComboboxRoot: R.ComboboxRoot,
    } as any,
    data() {
      return {
        active: false,
        localError: this.error,
      };
    },
    props: {
      modelValue: {
        default: '',
        required: true,
      },
      option: {
        type: String,
        default: '',
      },
      disableOption: {
        type: Boolean,
        default: false,
      },
      options: {
        type: Array as () => any[],
        default: () => [],
      },
      placeholder: String,
      optionPlaceholder: { type: String, default: 'Select ...' },
      type: {
        type: String,
        default: 'text',
      },
      error: String,
      max: Number,
      min: Number,
      reverse: Boolean,
    },
    computed: {
      formattedValue(): string {
        if (this.type === 'number' && this.modelValue !== undefined) {
          const numericValue = Number(this.modelValue);
          if (!isNaN(numericValue) && numericValue > 0) {
            return numericValue.toLocaleString();
          }
        }
        return this.modelValue;
      },
    },
    methods: {
      verification() {
        const max: any = this.max;
        if (parseInt(this.modelValue) > max) {
          this.localError = `Value must be less than ${max + 1}`;
        } else {
          this.localError = '';
          this.$emit('enter', this.option, this.modelValue);
        }
      },
      displayValue(value: string): string {
        return this.options.find((e: any) => e.value === value)?.label ?? '';
      },
      handleInput(event: Event) {
        const target = event.target as HTMLInputElement;
        let value = target.value;

        if (this.type === 'number') {
          value = value.replace(/,/g, '');
          if (!/^\d+$/.test(value)) {
            this.localError = 'Value must be a number';
          } else {
            this.localError = '';
          }
        }

        // Emit the raw value
        this.$emit('update:modelValue', value);
        this.$emit('change', value);
      },
    },
    watch: {
      error(newError: string) {
        this.localError = newError;
      },
    },
  };
</script>

<style lang="scss">
  @import '@/assets/styles/variables';

  .WDropdownTextfieldContent.ComboboxContent {
    z-index: 10;
    max-height: 300px;
    min-width: var(--radix-popper-anchor-width);
    overflow: hidden;

    background-color: $white;
    border-radius: 0.5rem;
    border: 1px solid $input-border;
    margin-block: 0.25rem;
    box-shadow: 0 2px 5px $neutral-gray-10;
  }
</style>

<style lang="scss" scoped>
  @import '@/assets/styles/variables.scss';

  .ComboboxAnchor {
    height: 3.5rem;
    width: 100%;
    max-width: 240px;
    position: relative;
    outline: none;

    &.optionDisabled {
      .ComboboxTrigger {
        background-color: $bg-dark;
      }

      * {
        cursor: not-allowed;
      }
    }
  }
  .ComboboxTrigger {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 100%;
    width: 100%;
    overflow: hidden;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    gap: 0.5rem;
  }

  .ComboboxInput {
    border: none;
    height: 100%;
    width: 100%;
    font-size: 0.875rem;
    color: $neutral-black;
    flex: 1;

    &:focus-visible {
      outline: none;
    }
  }

  .ComboboxIcon {
    font-size: 1rem;
    color: $neutral-gray;
  }

  [data-radix-combobox-viewport] {
    scrollbar-width: 8px;
  }

  .ComboboxEmpty {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    text-align: center;
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 500;
    color: $input-placeholder;
  }

  .ComboboxItem {
    max-width: 100%;
    gap: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 0.5rem;
    word-break: break-all;
    font-weight: 500;
    font-size: 0.875rem;
  }

  .ComboboxItem[data-disabled] {
    color: $input-placeholder;
    pointer-events: none;
  }

  .ComboboxItem[data-highlighted],
  .ComboboxItem:hover {
    outline: none;
    background-color: $bg-dark;
  }

  // non-radix code

  .dropdown-textfield__wrapper {
    .dropdown-textfield__error {
      padding: 0.5rem 1rem;
    }

    .dropdown-textfield {
      height: 3.5rem;
      outline: 1px solid $input-border;
      background-color: $white;
      display: flex;
      align-items: center;
      border-radius: 0.5rem;
      overflow: hidden;

      &.reverse {
        flex-direction: row-reverse;
      }

      .input-wrapper {
        width: 100%;
        height: 100%;
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        label {
          padding: 1rem;
          position: absolute;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          z-index: 1;
          color: $input-placeholder;
          pointer-events: none;
          font-size: 0.875rem;
          transition: all 0.2s;
        }

        input {
          position: absolute;
          caret-color: $var-primary-main;
          width: 100%;
          height: 100%;
          border: none;
          padding: 1rem;
          font-size: 0.875rem;

          &:focus-visible {
            outline: none;
          }
        }
        &.focused {
          label {
            opacity: 0.6;
            height: 1.5rem;
            padding: 0.375rem 1rem 0 1rem;
            font-size: 0.75rem;
            inset: 0 0 auto 0;
          }
          input {
            padding-top: 1.5625rem;
          }
        }
      }
    }
  }
</style>
